import { useEffect } from "react";
import { create } from "zustand";

/**
 * Primary notifications: the short, loud messages every player at the table
 * should see, whoever's turn it is — "FIVE OUT", "2 BONUS", a house rule coming
 * into force.
 *
 * They are deliberately one thing with one look. These messages compete for the
 * same moment and the same patch of screen, so if each were styled where it was
 * raised they would drift apart and two could end up on top of each other. One
 * queue means one at a time, in order, in one voice.
 *
 * NOT for anything addressed to a single viewer — "Your turn" is a different
 * kind of message and stays its own thing (`TurnPopup`).
 *
 * The store is module-level rather than per-screen because the loudest source is
 * the Pixi renderer, which is not a React tree. It already reaches for
 * `audio/sound` the same way.
 */
export interface PrimaryNotice {
  /** Distinguishes consecutive identical messages, so the second one re-animates. */
  id: number;
  /** The wordmark. Short — this is read at a glance or not at all. */
  title: string;
  /** Optional second line, for a message that genuinely needs one. */
  detail?: string;
  /**
   * How long it is on screen, start to finish.
   *
   * For anything raised from inside an animation this MUST be the duration that
   * animation budgets for it: `engine/animationCost.ts` mirrors those numbers to
   * the Go server to work out how long a turn's animation delays the next player
   * (docs/TURN_TIMER.md). A notification that outlives its budget is a turn timer
   * that starts before the table has finished talking.
   */
  durationMs: number;
  /** Size relative to the "FIVE OUT" wordmark, which is 1. */
  scale: number;
}

export type NoticeRequest = Omit<PrimaryNotice, "id" | "scale"> & { scale?: number };

interface NoticeState {
  queue: PrimaryNotice[];
  notify: (notice: NoticeRequest) => void;
  dismiss: (id: number) => void;
  clear: () => void;
}

let nextId = 1;

export const useNotices = create<NoticeState>((set) => ({
  queue: [],
  notify: (notice) =>
    set((s) => ({ queue: [...s.queue, { ...notice, scale: notice.scale ?? 1, id: nextId++ }] })),
  dismiss: (id) => set((s) => ({ queue: s.queue.filter((n) => n.id !== id) })),
  clear: () => set({ queue: [] }),
}));

/** Raises a notification from outside React — the Pixi renderer's way in. */
export function notify(notice: NoticeRequest): void {
  useNotices.getState().notify(notice);
}

/**
 * Drops anything queued or on screen. Called when the board is force-synced or
 * torn down: the message described a move that is no longer being animated, and
 * leaving it up would caption the wrong board.
 */
export function clearNotices(): void {
  useNotices.getState().clear();
}

/**
 * Announces Vinny's Rule the moment it starts applying, and not again until it
 * has lapsed and come back.
 *
 * Both play screens call this, which is why the wording and the timing live here
 * rather than in either of them — a rule that announced itself differently
 * online and locally would be two rules as far as a player is concerned. All the
 * screens contribute is when it applies, which only they can know.
 *
 * Unlike the messages the renderer raises, this one is not part of any
 * animation's budget: nothing is waiting on it, so its length is a reading
 * speed rather than a contract.
 */
export function useVinnysRuleNotice(applies: boolean): void {
  useEffect(() => {
    if (!applies) return;
    notify({ title: "Vinny’s Rule", durationMs: 1520 });
  }, [applies]);
}

/**
 * Tells the viewer their own deck is alight and what to do about it, once per
 * ignition.
 *
 * Addressed to one person, unlike everything else in this queue — but it is
 * raised here rather than as a TurnPopup because it competes for the same beat
 * as the rest and has to queue behind them. Nothing else on the table explains
 * that a burning deck is a control: the flames say something has happened, and
 * only this says what the player can now do.
 *
 * Both play screens call it, so the wording is one thing across local and
 * online play. Not part of any animation's budget — nothing waits on it.
 */
export function useStreakReadyNotice(ready: boolean, blueShell = false): void {
  useEffect(() => {
    if (!ready) return;
    notify({
      title: "You’re on fire",
      // Two different instructions, because they are two different actions: one
      // tap spends it under Blue Shell, and saying "pick who" there would send
      // the player looking for a choice the rule has already made.
      detail: blueShell ? "tap your deck to lock the leader" : "tap your deck, then pick who to lock",
      durationMs: 2200,
      scale: 0.72,
    });
  }, [ready, blueShell]);
}
